import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { t, translateError } from '../../i18n.js';
import { authState, selectClub, goToAdmin, api } from '../../state/auth.js';
import { loadTheme } from '../../state/theme.js';
import { loadNotifications, connectSocket } from '../../state/notifications.js';
import { route } from 'preact-router';

export default function ClubSelector() {
  const { clubs, isPlatformAdmin } = authState.value;
  const [selectingId, setSelectingId] = useState(null);
  const [goingToAdmin, setGoingToAdmin] = useState(false);
  const [error, setError] = useState('');
  // Clubs whose subscription ended: they're gone from the list, so say why
  const [ended, setEnded] = useState([]);
  useEffect(() => { api('/api/auth/me').then(d => setEnded(d.endedClubs || [])).catch(() => {}); }, []);
  const endedNote = ended.length > 0 && (
    <div class="alert" style="margin-bottom:16px;text-align:left;background:#fffbeb;border:1px solid #fcd34d;color:#78350f">{t('club.endedClubs', { clubs: ended.map(c => c.name).join(', ') })}</div>
  );

  const handleSelect = async (clubId) => {
    if (selectingId || goingToAdmin) return;
    setSelectingId(clubId);
    setError('');
    try {
      await selectClub(clubId);
      try { await loadTheme(); } catch (_) {}
      try { connectSocket(); } catch (_) {}
      try { loadNotifications(); } catch (_) {}
      route('/dashboard');
    } catch (err) {
      setError(translateError(err.code, err.detail));
      setSelectingId(null);
    }
  };

  const handleGoToAdmin = async () => {
    if (selectingId || goingToAdmin) return;
    setGoingToAdmin(true);
    try {
      await goToAdmin();
      route('/admin');
    } catch {
      setGoingToAdmin(false);
    }
  };

  if (!isPlatformAdmin && (!clubs || clubs.length === 0)) {
    return (
      <div class="club-selector">
        <h1 class="page-title mb-2">{t('club.selectClub')}</h1>
        <div style="text-align:center;padding:48px 0;color:var(--color-text-muted)">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom:16px;opacity:0.4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
          </svg>
          {endedNote || <p style="font-size:15px">{t('club.noClubs')}</p>}
        </div>
      </div>
    );
  }

  return (
    <div class="club-selector">
      <h1 class="page-title mb-2">{t('club.selectClub')}</h1>
      <p class="text-muted mb-6">{t('club.selectClubSubtitle')}</p>
      {endedNote}

      {error && <div class="alert alert-error" style="margin-bottom:16px">{error}</div>}

      {isPlatformAdmin && (
        <div
          class="club-card"
          onClick={handleGoToAdmin}
          style={`cursor:${goingToAdmin ? 'not-allowed' : 'pointer'};opacity:${goingToAdmin ? 0.6 : 1};border:2px solid var(--color-primary);margin-bottom:8px`}
        >
          <div style="width:40px;height:40px;border-radius:8px;background:var(--color-primary);display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:20px">
            ⚙️
          </div>
          <div style="flex:1;min-width:0">
            <div style="font-weight:600;font-size:15px">Beheerders omgeving</div>
            <div class="text-sm text-muted">Clubs, instellingen en platformbeheer</div>
          </div>
          <div class="club-card-chevron" style="flex-shrink:0">
            {goingToAdmin ? (
              <span style="display:inline-block;width:18px;height:18px;border:2px solid var(--color-border);border-top-color:var(--color-primary);border-radius:50%;animation:btn-spin 0.6s linear infinite" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
              </svg>
            )}
          </div>
        </div>
      )}

      {clubs && clubs.length > 0 && isPlatformAdmin && (
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--color-text-muted);margin:12px 0 6px">
          Of ga naar een club
        </div>
      )}

      {clubs.map(club => {
        const isLoading = selectingId === club.id;
        const isDisabled = !!selectingId;
        const initial = club.name?.[0]?.toUpperCase() ?? '?';

        return (
          <div
            class="club-card"
            key={club.id}
            onClick={() => !isDisabled && handleSelect(club.id)}
            style={isDisabled ? 'opacity:0.6;cursor:not-allowed' : 'cursor:pointer'}
            aria-disabled={isDisabled}
          >
            {club.logo_path ? (
              <img src={club.logo_path} alt={club.name} style="width:40px;height:40px;border-radius:8px;object-fit:cover;flex-shrink:0" />
            ) : (
              <div
                class="sidebar-brand-initial"
                style={club.primary_color ? `background:${club.primary_color}` : undefined}
              >
                {initial}
              </div>
            )}

            <div style="flex:1;min-width:0">
              <div style="font-weight:600;font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{club.name}</div>
              <div class="text-sm text-muted">{club.slug}</div>
            </div>

            <div class="club-card-chevron" style="flex-shrink:0">
              {isLoading ? (
                <span style="display:inline-block;width:18px;height:18px;border:2px solid var(--color-border);border-top-color:var(--color-primary);border-radius:50%;animation:btn-spin 0.6s linear infinite" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                </svg>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
